import { IPaginationParams } from 'services/types';
import { toUserMessage } from 'utils/apiErrorHelper';
import { api } from '../DefaultService';

import {
  IOccurrenceCreateParams,
  IOccurrenceFilterParams,
  IOccurrenceSummaryProps,
  IOccurrenceDetailProps,
  IOccurrenceUpdateParams,
  IOccurrenceActionParams,
  IOccurrenceStudentLookupProps,
  IOccurrenceResponsibleLookupProps,
  IOccurrenceGeneralLookupProps,
  IOccurrenceVehicleLookupProps,
  IOccurrenceBlockedLogProps,
  IOccurrenceSummaryCountsProps,
} from './types';

const ENDPOINT = 'v1/occurrence';

const fetchData = async <T>(promise: Promise<import('axios').AxiosResponse<T>>): Promise<T> => {
  try {
    const response = await promise;
    return response.data;
  } catch (error: any) {
    throw new Error(toUserMessage(error));
  }
};

export const createOccurrenceAsync = ({ data }: IOccurrenceCreateParams): Promise<IOccurrenceDetailProps> => {
  const formData = new FormData();

  formData.append('Type', data.type.toString());
  formData.append('Severity', data.severity.toString());
  formData.append('Title', data.title);

  if (data.description) formData.append('Description', data.description);
  if (data.subjectRecognitionId) formData.append('SubjectRecognitionId', data.subjectRecognitionId.toString());
  if (data.vehiclePlate) formData.append('VehiclePlate', data.vehiclePlate);
  
  if (data.relatedStudentIds && data.relatedStudentIds.length > 0) {
    data.relatedStudentIds.forEach((id) => {
      formData.append('RelatedStudentIds', id.toString());
    });
  }
  
  if (data.attachment) {
    formData.append('Attachment', data.attachment);
  }

  return fetchData(
    api.post(ENDPOINT, formData, {
      headers: { 'Content-Type': undefined },
    })
  );
};

export const getOccurrenceByIdAsync = (id: number): Promise<IOccurrenceDetailProps> => {
  return fetchData(api.get(`${ENDPOINT}/${id}`));
};

export const getAllOccurrenceByLicenseAsync = (
  pagination?: IPaginationParams,
  filter?: IOccurrenceFilterParams,
): Promise<IOccurrenceSummaryProps[]> => {
  return fetchData(api.get(`${ENDPOINT}/getAllByLicense`, { params: { ...pagination, ...filter } }));
};

export const getAllOccurrenceCountAsync = (filter?: IOccurrenceFilterParams): Promise<number> => {
  return fetchData(api.get(`${ENDPOINT}/getAllByLicense/count`, { params: { ...filter } }));
};

export const getAllOccurrenceSummaryAsync = (searchTerm?: string): Promise<IOccurrenceSummaryCountsProps> => {
  return fetchData(api.get(`${ENDPOINT}/getAllByLicense/summary`, { params: { searchTerm } }));
};

export const updateOccurrenceAsync = ({ id, data }: IOccurrenceUpdateParams): Promise<IOccurrenceDetailProps> => {
  return fetchData(api.put(`${ENDPOINT}/${id}`, data));
};

export const deleteOccurrenceAsync = (id: number): Promise<void> => {
  return fetchData(api.delete(`${ENDPOINT}/${id}`));
};

export const registerOccurrenceActionAsync = ({
  occurrenceId,
  id,
  data,
}: IOccurrenceActionParams): Promise<IOccurrenceDetailProps> => {
  const targetId = occurrenceId || id;
  return fetchData(api.post(`${ENDPOINT}/${targetId}/action`, data));
};

export const checkCriticalAlertAsync = (recognitionId: number): Promise<IOccurrenceSummaryProps | null> => {
  return fetchData(api.get(`${ENDPOINT}/check-alert/recognition/${recognitionId}`));
};

export const getStudentsForOccurrenceAsync = (): Promise<IOccurrenceStudentLookupProps[]> => {
  return fetchData(api.get(`${ENDPOINT}/lookup/students`));
};

export const getResponsiblesForOccurrenceAsync = (): Promise<IOccurrenceResponsibleLookupProps[]> => {
  return fetchData(api.get(`${ENDPOINT}/lookup/responsibles`));
};

export const getGeneralsForOccurrenceAsync = (): Promise<IOccurrenceGeneralLookupProps[]> => {
  return fetchData(api.get(`${ENDPOINT}/lookup/generals`));
};

export const getVehiclesForOccurrenceAsync = (): Promise<IOccurrenceVehicleLookupProps[]> => {
  return fetchData(api.get(`${ENDPOINT}/lookup/vehicles`));
};

export const getOccurrenceStreamEndpoint = (): string => `${ENDPOINT}`;

export const getBlockedTurnstileLogsAsync = (
  pagination?: IPaginationParams,
  searchTerm?: string,
): Promise<IOccurrenceBlockedLogProps[]> => {
  return fetchData(api.get(`${ENDPOINT}/logs/blocked`, { params: { ...pagination, searchTerm } }));
};

export const getBlockedTurnstileLogsCountAsync = (searchTerm?: string): Promise<number> => {
  return fetchData(api.get(`${ENDPOINT}/logs/blocked/count`, { params: { searchTerm } }));
};